/**
 * Flat command catalog derived from the ribbon config.
 *
 * The menu bar, command palette and Console all walk this list so a new ribbon
 * button is searchable without a second registry.
 */

import { RIBBON_TABS } from "@/ribbon/ribbon.config";
import type { RibbonContext, RibbonItem, RibbonTab } from "@/ribbon/types";
import type { TabId } from "@/shell/tabRegistry";

export interface CommandEntry {
  id: string;
  label: string;
  /** "Home › Case › Open Subject" */
  path: string;
  action: string;
  tabId?: TabId;
  group: string;
  disabled?: (ctx: RibbonContext) => boolean;
}

const TAB_IDS = new Set<string>([
  "file",
  "home",
  "pipeline",
  "preprocess",
  "registration",
  "segmentation",
  "perfusion",
  "quantification",
  "view",
  "results",
  "developer",
]);

function isTabId(id: string): id is TabId {
  return TAB_IDS.has(id);
}

function walkItem(tab: RibbonTab, groupLabel: string, item: RibbonItem, out: CommandEntry[]): void {
  out.push({
    id: item.id,
    label: item.label,
    path: `${tab.label} › ${item.submenu ? `${groupLabel} › ${item.submenu}` : groupLabel} › ${item.label}`,
    action: item.action,
    tabId: isTabId(tab.id) ? tab.id : undefined,
    group: groupLabel,
    disabled: item.disabled,
  });
  for (const entry of item.dropdown ?? []) {
    out.push({
      id: entry.id,
      label: entry.label,
      path: `${tab.label} › ${groupLabel} › ${item.label} › ${entry.label}`,
      action: entry.action,
      tabId: isTabId(tab.id) ? tab.id : undefined,
      group: groupLabel,
      disabled: item.disabled,
    });
  }
}

let cached: CommandEntry[] | null = null;

export function listCommands(): CommandEntry[] {
  if (cached) return cached;
  const out: CommandEntry[] = [];
  for (const tab of RIBBON_TABS) {
    for (const group of tab.groups) {
      for (const item of group.items) walkItem(tab, group.label, item, out);
    }
  }
  out.push(
    {
      id: "shell.commandPalette",
      label: "Command Palette",
      path: "Help › Command Palette",
      action: "shell.commandPalette",
      group: "Help",
    },
    {
      id: "shell.shortcuts",
      label: "Keyboard Shortcuts",
      path: "Help › Keyboard Shortcuts",
      action: "shell.shortcuts",
      group: "Help",
    },
    {
      id: "ribbon.pin",
      label: "Pin Ribbon",
      path: "Help › Pin Ribbon",
      action: "ribbon.pin",
      group: "Help",
    },
    {
      id: "ribbon.collapse",
      label: "Collapse Ribbon",
      path: "Help › Collapse Ribbon",
      action: "ribbon.collapse",
      group: "Help",
    },
    {
      id: "ribbon.autohide",
      label: "Auto-hide Ribbon",
      path: "Help › Auto-hide Ribbon",
      action: "ribbon.autohide",
      group: "Help",
    },
    {
      id: "shell.about",
      label: "About ADS Desk",
      path: "Help › About",
      action: "shell.about",
      group: "Help",
    },
  );
  cached = out;
  return out;
}

export function searchCommands(query: string): CommandEntry[] {
  const q = query.trim().toLowerCase();
  const all = listCommands();
  if (!q) return all.slice(0, 40);
  return all
    .filter((c) => `${c.label} ${c.path} ${c.action}`.toLowerCase().includes(q))
    .slice(0, 40);
}
